import { sortExpr } from './sort'
import type { Plan, PlanStatus } from '@thom/folio-domain/core/domain/plan'
import { planId as toPlanId } from '@thom/folio-domain/core/domain/plan'
import { issueId as toIssueId } from '@thom/folio-domain/core/domain/issue'
import { projectId as toProjectId, userId as toUserId } from '@thom/folio-domain/core/domain/project'
import type { PlanFilter, PlansPort } from '@thom/folio-domain/core/ports/plans'
import { err, ok, type Result } from '@thom/folio-domain/lib/result'
import { tryCatch } from '@thom/folio-domain/lib/try-catch'
import { Collections, type JournPlansResponse } from '@thom/folio-ui/pocketbase-types'
import { getPocketBaseClient } from './client'
import { keyed } from './request-key'
import { filterFor } from '@thom/pb/filter-builder'
import { projectClauses } from './project-clauses'
import { countRows } from './count-rows'
import { paginate } from './paginate'

type PlanRecord = JournPlansResponse<string[]>

type PlanColumns = {
	'project.slug': string
	'project.domain.slug': string
	'project.domain.client.slug': string
	id: string
	issue: string
	title: string
	goal: string
	status: PlanStatus
	tags: string
	created: Date
	updated: Date
}

const toPlan = (record: PlanRecord): Plan => ({
	id: toPlanId(record.id),
	projectId: toProjectId(record.project),
	ticketId: record.issue ? toIssueId(record.issue) : undefined,
	title: record.title,
	goal: record.goal ?? '',
	status: record.status as PlanStatus,
	tags: record.tags ?? [],
	createdBy: record.created_by ? toUserId(record.created_by) : undefined,
	createdAt: new Date(record.created),
	updatedAt: new Date(record.updated),
})

const columns = (project: string, filter: PlanFilter) =>
	filterFor<PlanColumns>()([
		...projectClauses(project),
		{ field: 'id', comparator: 'eq', value: filter.id },
		{ field: 'issue', comparator: 'eq', value: filter.ticketId },
		{ field: 'status', comparator: 'anyOf', value: filter.status },
		{ field: 'tags', comparator: 'containsAll', value: filter.tags },
		{ field: 'title', comparator: 'contains', value: filter.search },
	])

const createPlansAdapter = (): PlansPort => {
	const client = getPocketBaseClient()
	const collection = client.collection(Collections.JournPlans)

	return {
		count: async (project, filter = {}): Promise<Result<number>> => {
			const { expr, params } = columns(project, filter)

			const { data, error } = await tryCatch(
				countRows(collection, keyed('plans.count', { filter: client.filter(expr, params) })),
			)

			return error ? err(new Error(`Failed to count plans: ${error.message}`, { cause: error })) : ok(data)
		},

		get: async (project, id): Promise<Result<Plan>> => {
			const { expr, params } = filterFor<PlanColumns>()([
				...projectClauses(project),
				{ field: 'id', comparator: 'eq', value: id },
			])

			const { data, error } = await tryCatch(
				collection.getFirstListItem<PlanRecord>(client.filter(expr, params), keyed('plans.get', {})),
			)

			return error ? err(new Error(`Failed to load plan ${id}: ${error.message}`, { cause: error })) : ok(toPlan(data))
		},

		list: async (project, filter = {}): Promise<Result<readonly Plan[]>> => {
			const { expr, params } = columns(project, filter)

			const { data, error } = await tryCatch(
				paginate<PlanRecord>(
					collection,
					filter,
					keyed(
						'plans.list',
						{
							filter: client.filter(expr, params),
							sort: sortExpr(filter.sort, '-created'),
						},
						[filter.limit, filter.offset],
					),
				),
			)

			return error ? err(new Error(`Failed to list plans: ${error.message}`, { cause: error })) : ok(data.map(toPlan))
		},
	}
}

export { columns, createPlansAdapter, toPlan }
